/**
 * 定位權限的「先跳系統窗、拿不到才解釋」策略。
 * --------------------------------------------------------------------------
 * 第一次進流程直接呼叫 getCurrentPosition，讓系統自己的權限窗跳出來 —— 使用者只需要點一次。
 * 不做 pre-permission 卡片：那層卡片會讓使用者連續看到兩個對話框，感覺像同一件事被問兩遍。
 *
 * 只有在「已經知道定位拿不到」時（先前被拒 / Permissions API 回報 denied），才輪到我們自己的
 * 卡片出場說明用途。此時系統窗本來就不會再跳，所以永遠不會出現兩個窗連著跳的情況。
 *
 * 為什麼旗標放 localStorage 而非 sessionStorage：權限拒絕是跨分頁、跨次數持續的狀態，
 * 使用者這次拒絕，下次從 LINE 重開 LIFF（新的 session）我們仍要記得要改彈說明卡。
 */

import { isPracticeMode } from "@utils/practice/practiceMode";

const DENIED_KEY = "locateDenied";

/**
 * 客服練習模式改用 sessionStorage：練習時客服很可能隨手按「不允許」，若寫進 localStorage
 * 就會跨分頁長期留著，之後每次練習都直接看到說明卡而不是系統窗 —— 客服會以為正式流程長那樣，
 * 進而教錯客戶。收在 session 裡，關掉分頁就乾淨。正式客戶不走這條，行為與原本一致。
 */
const store = (): Storage => (isPracticeMode() ? sessionStorage : localStorage);

/** storage 在 Safari 私密模式 / 停用 cookie 時會丟例外，全部包起來，讀不到就當「沒拒絕過」。 */
const safeGet = (key: string): string | null => {
  try {
    return store().getItem(key);
  } catch {
    return null;
  }
};
const safeSet = (key: string, value: string) => {
  try {
    store().setItem(key, value);
  } catch {
    // 無法寫入 → 退化成「每次都直接跳系統窗」，不影響正確性
  }
};
const safeRemove = (key: string) => {
  try {
    store().removeItem(key);
  } catch {
    // 同上
  }
};

/** 使用者拒絕過定位（或定位失敗）→ 下次改彈說明卡而不是直接叫系統窗。 */
export const markLocateDenied = () => safeSet(DENIED_KEY, "1");

/** 定位成功 → 清掉旗標，之後恢復「直接跳系統窗」。 */
export const clearLocateDenied = () => safeRemove(DENIED_KEY);

export const wasLocateDenied = (): boolean => safeGet(DENIED_KEY) === "1";

export type LocatePermissionState = "granted" | "denied" | "prompt" | "unknown";

/**
 * 查目前的定位權限狀態。
 *
 * Permissions API 在 iOS WKWebView（LINE in-app browser）的支援度不一致 —— 可能整個缺席、
 * 也可能不認 "geolocation" 這個 name 而丟例外，所以一律 feature-detect + try/catch，
 * 查不到就回 "unknown"，由呼叫端搭配 wasLocateDenied() 這個自己記的旗標決定。
 */
export const probeLocatePermission = async (): Promise<LocatePermissionState> => {
  if (typeof navigator === "undefined" || !navigator.permissions?.query) return "unknown";
  try {
    const status = await navigator.permissions.query({ name: "geolocation" as PermissionName });
    return status.state as LocatePermissionState;
  } catch {
    return "unknown";
  }
};

/** 這次進流程該怎麼開場：直接叫系統窗，還是先彈我們的說明卡。 */
export type LocateGate = "auto" | "ask";

/**
 * "auto"：直接呼叫 getCurrentPosition（可能跳系統窗，也可能已授權而靜默成功）。
 * "ask" ：已知拿不到位置 → 先彈我們的卡片說明用途，由使用者決定要不要再試。
 */
export const resolveLocateGate = async (): Promise<LocateGate> => {
  const state = await probeLocatePermission();
  if (state === "granted") return "auto"; // 已授權 → 靜默取得，不會有任何窗
  if (state === "denied") return "ask"; // 系統窗不會再跳，只能由我們說明
  // prompt / unknown → 沒有可靠的權限資訊，改看我們自己記的拒絕紀錄
  return wasLocateDenied() ? "ask" : "auto";
};
